import type {} from "@mui/x-date-pickers/themeAugmentation";
import {
  createTheme,
  responsiveFontSizes,
  shouldSkipGeneratingVar,
} from "@mui/material/styles";
import type { Theme } from "@mui/material/styles";
import { resolveBrandTheme } from "@/helpers/theme/resolveBrandTheme";
import {
  DEFAULT_THEME_TOKENS,
  cssTokenName,
  type AppThemeTokens,
  type ThemeTokenKey,
} from "./themeTokens";
import type {} from "@mui/material/themeCssVarsAugmentation";

// Preserve the public CSS names without placing custom style tokens in MUI's
// reserved font/spacing fields. MUI joins path segments with hyphens.
type Hyphenated<Key extends string> = Key extends `${infer Head}_${infer Tail}`
  ? `${Head}-${Hyphenated<Tail>}`
  : Key;
type TokenGroup<Group extends string> = {
  [Key in ThemeTokenKey as Key extends `${Group}_${infer Role}`
    ? Hyphenated<Role>
    : never]: string;
};
type TokenNamespace =
  | "color"
  | "neutral"
  | "primary"
  | "status"
  | "surface"
  | "radius";
type RootTokens = {
  [Key in ThemeTokenKey as Key extends `${TokenNamespace}_${string}`
    ? never
    : Hyphenated<Key>]: string;
};
type MuiTokenNamespaces = RootTokens & {
  color: TokenGroup<"color">;
  neutral: TokenGroup<"neutral">;
  primary: TokenGroup<"primary">;
  status: TokenGroup<"status">;
  surface: TokenGroup<"surface">;
  radius: TokenGroup<"radius">;
};
function toMuiTokenNamespaces(tokens: AppThemeTokens): MuiTokenNamespaces {
  const namespaces: Record<string, string | Record<string, string>> = {
    color: {},
    neutral: {},
    primary: {},
    status: {},
    surface: {},
    radius: {},
  };
  for (const [key, value] of Object.entries(tokens)) {
    const name = cssTokenName(key).slice(2);
    const group = (
      ["color", "neutral", "primary", "status", "surface", "radius"] as const
    ).find((namespace) => key.startsWith(`${namespace}_`));
    if (group)
      (namespaces[group] as Record<string, string>)[
        name.slice(group.length + 1)
      ] = value;
    else namespaces[name] = value;
  }
  return namespaces as MuiTokenNamespaces;
}

// Shared palette extensions used by the CSS-variable theme and UI controls.
declare module "@mui/material/styles" {
  interface PaletteColor {
    tint?: string;
    tintHover?: string;
    brand?: string;
  }

  interface SimplePaletteColorOptions {
    tint?: string;
    tintHover?: string;
    brand?: string;
  }

  interface Theme extends MuiTokenNamespaces {
    appTokens: AppThemeTokens;
  }
  interface ThemeOptions extends Partial<MuiTokenNamespaces> {
    appTokens?: AppThemeTokens;
  }

  interface ThemeVars extends MuiTokenNamespaces {}

  interface TypeText {
    placeholder: string;
  }

  interface Palette {
    neutral: Record<200 | 300 | 500 | 600 | 700, string>;
    surface: { dark: string };
    navigation: {
      surface: string;
      text: string;
      hoverSurface: string;
      selectedSurface: string;
    };
    table: { header: string };
  }

  interface PaletteOptions {
    neutral?: Record<200 | 300 | 500 | 600 | 700, string>;
    surface?: { dark: string };
    navigation?: {
      surface: string;
      text: string;
      hoverSurface: string;
      selectedSurface: string;
    };
    table?: { header: string };
  }
}

type ThemeWithMutableComponents = Theme & {
  palette: Theme["palette"] & {
    primary: Theme["palette"]["primary"] & Record<string, string>;
    secondary: Theme["palette"]["secondary"] & Record<string, string>;
  };
  components: NonNullable<Theme["components"]>;
};

const createMuiTheme = (customTheme: unknown = null) => {
  const { tokens } = resolveBrandTheme(customTheme);
  const primaryMain = tokens.primary_base;
  const primaryHover = tokens.primary_base_hover;
  const primaryTint = tokens.primary_tint;
  const primaryTintHover = tokens.primary_tint_hover;
  const brandColor = tokens.primary_brand;
  const secondaryMain = tokens.surface_dark;
  const surfaceDarkHover = tokens.surface_dark_hover;
  const neutral700 = tokens.neutral_700;
  const neutral600 = tokens.neutral_600;
  const neutral500 = tokens.neutral_500;
  const neutral200 = tokens.neutral_200;
  const neutralWhite = tokens.neutral_white;

  const baseTheme = createTheme({
    ...toMuiTokenNamespaces(tokens),
    cssVariables: {
      cssVarPrefix: "",
      shouldSkipGeneratingVar: (keys) =>
        keys[0] === "appTokens" || shouldSkipGeneratingVar(keys),
    },
    appTokens: tokens,
    shadows: Array(25).fill("none") as Theme["shadows"],
    palette: {
      background: {
        default: tokens.neutral_50,
        paper: neutralWhite,
      },
      common: { white: neutralWhite },
      surface: { dark: tokens.surface_dark },
      navigation: {
        surface: tokens.surface_dark,
        text: tokens.color_menu_text,
        hoverSurface: surfaceDarkHover,
        selectedSurface: surfaceDarkHover,
      },
      table: { header: neutral700 },
      // Standard alert banners use status tokens instead of MUI's mixes.
      Alert: Object.fromEntries(
        (["error", "warning", "success", "info"] as const).flatMap((status) => [
          [`${status}StandardBg`, tokens[`status_${status}_tint`]],
          [`${status}Color`, tokens[`status_${status}`]],
        ])
      ),
      divider: neutral200,
      action: {
        active: neutral500,
        hover: tokens.neutral_100,
        selected: primaryTint,
        disabled: neutral500,
        disabledBackground: neutral200,
      },
      primary: {
        main: primaryMain,
        contrastText: neutralWhite,
        light: primaryTint,
        dark: primaryHover,
        tintHover: primaryTintHover,
        brand: brandColor,
      } as Record<string, string>,
      secondary: {
        main: secondaryMain,
        contrastText: neutralWhite,
        dark: surfaceDarkHover,
      } as Record<string, string>,
      error: {
        contrastText: neutralWhite,
        main: tokens.status_error,
        dark: tokens.status_error_hover,
        light: tokens.status_error_tint,
        tint: tokens.status_error_tint,
      },
      warning: {
        contrastText: neutralWhite,
        main: tokens.status_warning,
        light: tokens.status_warning_tint,
        tint: tokens.status_warning_tint,
      },
      success: {
        contrastText: neutralWhite,
        main: tokens.status_success,
        light: tokens.status_success_tint,
        tint: tokens.status_success_tint,
      },
      info: {
        contrastText: neutralWhite,
        main: tokens.status_info,
        light: tokens.status_info_tint,
        tint: tokens.status_info_tint,
      },
      neutral: {
        700: neutral700,
        600: neutral600,
        500: neutral500,
        300: tokens.neutral_300,
        200: neutral200,
      },
      text: {
        primary: neutral700,
        secondary: neutral600,
        disabled: neutral500,
        placeholder: neutral600,
      },
    } as Record<string, unknown>,
    typography: {
      fontFamily: [
        tokens.font_family,
        "Montserrat",
        "Roboto",
        "Helvetica",
        "Arial",
        "sans-serif",
        "-apple-system",
        "BlinkMacSystemFont",
        '"Segoe UI"',
        '"Helvetica Neue"',
        '"Apple Color Emoji"',
        '"Segoe UI Emoji"',
        '"Segoe UI Symbol"',
      ].join(","),
      h1: { fontWeight: 700 },
      h2: { fontWeight: 700 },
      h3: { fontWeight: 700 },
      h4: { fontWeight: 700 },
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
    },
    shape: {
      borderRadius: Number.parseFloat(DEFAULT_THEME_TOKENS.radius_control),
    },
  });

  const theme = responsiveFontSizes(baseTheme) as ThemeWithMutableComponents;

  // Inputs, dropdowns and date pickers stay white in every enabled state.
  const inputSurface = {
    backgroundColor: theme.vars.neutral.white,
    "&.Mui-disabled": {
      backgroundColor: theme.vars.palette.action.disabledBackground,
    },
  };

  // Shared component defaults consume this resolved theme.
  theme.components = {
    MuiOutlinedInput: {
      styleOverrides: {
        input: {
          // Mask the UA autofill background and MUI's unscoped dark autofill fill.
          "&:-webkit-autofill": {
            WebkitBoxShadow: `0 0 0 1000px ${theme.vars.neutral.white} inset`,
            WebkitTextFillColor: theme.vars.palette.text.primary,
            caretColor: theme.vars.palette.text.primary,
          },
        },
        notchedOutline: { borderColor: theme.vars.palette.neutral[500] },
        root: {
          ...inputSurface,
          borderRadius: "var(--radius-control)",
          "&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiOutlinedInput-notchedOutline":
            { borderColor: "var(--neutral-600)" },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--primary-base)",
            borderWidth: 2,
          },
          "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline": {
            borderColor: "var(--status-error)",
          },
          "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
            borderColor: theme.vars.palette.action.disabledBackground,
          },
        },
      },
    },
    MuiPickersOutlinedInput: {
      styleOverrides: {
        notchedOutline: { borderColor: "var(--neutral-500)" },
        root: {
          ...inputSurface,
          "&:hover:not(.Mui-focused):not(.Mui-error):not(.Mui-disabled) .MuiPickersOutlinedInput-notchedOutline":
            { borderColor: "var(--neutral-600)" },
          "&.Mui-disabled .MuiPickersOutlinedInput-notchedOutline": {
            borderColor: "var(--neutral-200)",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          "&::placeholder": {
            color: "var(--palette-text-placeholder)",
            opacity: 1,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "var(--radius-control)",
          textTransform: "none",
          fontWeight: 500,
          "&.Mui-focusVisible::after": {
            content: '""',
            position: "absolute",
            inset: "-2px",
            borderRadius: "var(--radius-focus-ring)",
            border: "2px solid var(--primary-base)",
            boxSizing: "border-box",
            pointerEvents: "none",
          },
        },
        containedPrimary: {
          backgroundColor: "var(--primary-base)",
          "&:hover": {
            backgroundColor: "var(--primary-base-hover)",
          },
        },
        outlinedPrimary: {
          borderColor: "var(--primary-base)",
          "&:hover": {
            backgroundColor: "var(--primary-base-hover)",
            color: "var(--palette-primary-contrastText)",
          },
        },
      },
    },
    MuiIconButton: {
      styleOverrides: { root: { borderRadius: "var(--radius-control)" } },
    },
    MuiCheckbox: {
      styleOverrides: { root: { borderRadius: "var(--radius-control)" } },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          borderRadius: "var(--radius-control)",
          "&.Mui-selected": { backgroundColor: "var(--primary-tint)" },
          "&.Mui-selected:hover, &.Mui-selected.Mui-focusVisible": {
            backgroundColor: "var(--primary-tint-hover)",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: { root: { borderRadius: "var(--radius-control)" } },
    },
    MuiChip: {
      styleOverrides: { root: { borderRadius: "var(--radius-control)" } },
      variants: [
        {
          props: { variant: "filled", color: "default" },
          style: {
            backgroundColor: "var(--neutral-100)",
            "&.MuiChip-clickable:hover, &.Mui-focusVisible": {
              backgroundColor: "var(--neutral-200)",
            },
          },
        },
      ],
    },
    MuiAvatar: {
      styleOverrides: {
        circular: { borderRadius: "var(--radius-round)" },
        rounded: { borderRadius: "var(--radius-container)" },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        track: { borderRadius: "var(--radius-round)" },
        thumb: { borderRadius: "var(--radius-round)" },
      },
    },
    MuiToggleButton: {
      styleOverrides: { root: { borderRadius: "var(--radius-round)" } },
    },
    MuiBadge: {
      styleOverrides: { badge: { borderRadius: "var(--radius-round)" } },
    },
    MuiAlert: {
      styleOverrides: { root: { borderRadius: "var(--radius-container)" } },
    },
    MuiCard: {
      styleOverrides: { root: { borderRadius: "var(--radius-container)" } },
    },
    MuiAccordion: {
      styleOverrides: {
        rounded: {
          borderRadius: "var(--radius-container)",
          "&:first-of-type": {
            borderTopLeftRadius: "var(--radius-container)",
            borderTopRightRadius: "var(--radius-container)",
          },
          "&:last-of-type": {
            borderBottomLeftRadius: "var(--radius-container)",
            borderBottomRightRadius: "var(--radius-container)",
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          fontSize: ".92rem",
        },
        asterisk: {
          color: `${theme.vars.palette.error.main}!important`,
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        asterisk: {
          color: `${theme.vars.palette.error.main}!important`,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--shadow-overlay)",
        },
        scrollPaper: {
          marginTop: "30px",
        },
        scrollBody: {
          marginTop: "30px",
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--shadow-floating)",
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--shadow-floating)",
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--shadow-floating)",
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          borderRadius: "var(--radius-container)",
          boxShadow: "var(--shadow-floating)",
        },
        // Listbox (not option) overrides load after MUI's selected-row tint.
        listbox: {
          '& .MuiAutocomplete-option[aria-selected="true"]': {
            backgroundColor: "var(--primary-tint)",
          },
          '& .MuiAutocomplete-option[aria-selected="true"].Mui-focused, & .MuiAutocomplete-option[aria-selected="true"].Mui-focusVisible':
            { backgroundColor: "var(--primary-tint-hover)" },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: ({ ownerState }) => ({
          boxShadow:
            ownerState.variant === "temporary"
              ? "var(--shadow-overlay)"
              : "none",
        }),
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: ({ ownerState }) => ({
          borderRadius: ownerState.square
            ? "initial"
            : "var(--radius-container)",
          boxShadow: "none",
          ...(ownerState.variant === "elevation" &&
            (ownerState.elevation ?? 1) > 0 &&
            !/Mui(Dialog|Popover|Menu|Autocomplete|PickerPopper|Drawer)-paper/.test(
              ownerState.className || ""
            ) && {
              border: "1px solid var(--neutral-200)",
            }),
        }),
      },
    },
  };

  return theme;
};

// Create and export the default theme
const defaultTheme = createMuiTheme();
export default defaultTheme;

// Export the theme creation function
export { createMuiTheme };
